import type {
  ErrorType,
  ErrorTypeInput,
  ExitCriterionInput,
  ProjectSettings,
  SeverityLevel,
  SeverityLevelInput,
} from '@testflow/shared';
import { request } from './api.js';

const base = (p: string) => `/v1/projects/${p}`;

// Task B2 (#29 Finding 23): GET /defect-config reichert jede Zeile um usage_count an (wie viele
// Defects diese Severity/diesen Fehlertyp aktuell referenzieren). Bewusst NICHT am geteilten
// SeverityLevel/ErrorType-Typ selbst (packages/shared/src/schemas/defect.ts) angehängt — der wird
// an vielen weiteren Stellen (Board, Defect-Karte) konstruiert/konsumiert, wo usage_count nie
// befüllt wäre. Die Erweiterung bleibt lokal auf den Endpunkt beschränkt, der sie tatsächlich füllt.
export type SeverityLevelWithUsage = SeverityLevel & { usage_count: number };
export type ErrorTypeWithUsage = ErrorType & { usage_count: number };

// GET /defect-config liefert Severity-Stufen + Fehlertypen in einem Aufruf (siehe
// apps/api/src/routes/defect-config.ts) — die einzelnen Getter unten teilen sich diesen Call,
// damit ein Panel, das beide Listen braucht, nicht zweimal lädt.
export interface DefectConfig {
  severity_levels: SeverityLevelWithUsage[];
  error_types: ErrorTypeWithUsage[];
}

// Rohzeile aus der exit_criteria-Tabelle (Admin-Editor, Plan 7). Zu unterscheiden von
// `ExitCriterion` in packages/shared/src/schemas/report.ts, das den ausgewerteten Report
// (inkl. `met`/`actual`) für REP-5 beschreibt.
export interface ExitCriterionConfig extends ExitCriterionInput {
  criterion_id: string;
}

export const projectConfigApi = {
  getSettings(projectId: string): Promise<ProjectSettings> {
    return request<ProjectSettings>(`${base(projectId)}/config/settings`);
  },
  patchSettings(projectId: string, patch: Partial<ProjectSettings>): Promise<ProjectSettings> {
    return request<ProjectSettings>(`${base(projectId)}/config/settings`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },
  getDefectConfig(projectId: string): Promise<DefectConfig> {
    return request<DefectConfig>(`${base(projectId)}/defect-config`);
  },
  getSeverityLevels(projectId: string): Promise<SeverityLevelWithUsage[]> {
    return projectConfigApi.getDefectConfig(projectId).then((c) => c.severity_levels);
  },
  getErrorTypes(projectId: string): Promise<ErrorTypeWithUsage[]> {
    return projectConfigApi.getDefectConfig(projectId).then((c) => c.error_types);
  },
  putSeverityLevels(projectId: string, items: SeverityLevelInput[]): Promise<{ ok: boolean }> {
    return request<{ ok: boolean }>(`${base(projectId)}/config/severity-levels`, {
      method: 'PUT',
      body: JSON.stringify(items),
    });
  },
  putErrorTypes(projectId: string, items: ErrorTypeInput[]): Promise<{ ok: boolean }> {
    return request<{ ok: boolean }>(`${base(projectId)}/config/error-types`, {
      method: 'PUT',
      body: JSON.stringify(items),
    });
  },
  // Task B2 (#29 Finding 23): geführtes Umhängen statt stiller Null-Setzung — Muster wörtlich aus
  // dataKindsApi.merge (data-kinds-api.ts).
  reassignSeverity(
    projectId: string,
    severityId: string,
    targetId: string,
  ): Promise<{ ok: true; moved: number }> {
    return request<{ ok: true; moved: number }>(
      `${base(projectId)}/config/severity-levels/${severityId}/reassign`,
      { method: 'POST', body: JSON.stringify({ target_id: targetId }) },
    );
  },
  reassignErrorType(
    projectId: string,
    errorTypeId: string,
    targetId: string,
  ): Promise<{ ok: true; moved: number }> {
    return request<{ ok: true; moved: number }>(
      `${base(projectId)}/config/error-types/${errorTypeId}/reassign`,
      { method: 'POST', body: JSON.stringify({ target_id: targetId }) },
    );
  },
  getExitCriteria(projectId: string): Promise<ExitCriterionConfig[]> {
    return request<ExitCriterionConfig[]>(`${base(projectId)}/config/exit-criteria`);
  },
  putExitCriteria(projectId: string, items: ExitCriterionInput[]): Promise<{ ok: boolean }> {
    return request<{ ok: boolean }>(`${base(projectId)}/config/exit-criteria`, {
      method: 'PUT',
      body: JSON.stringify(items),
    });
  },
};
